import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SessionProvider } from './contexts/SessionContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Login } from './screens/Login';
import { AuthVerify } from './screens/AuthVerify';
import { Dashboard } from './screens/Dashboard';
import { DashboardSettings } from './screens/DashboardSettings';
import { IdeaCheck } from './screens/IdeaCheck';
import { IdeaSelection } from './screens/IdeaSelection';
import { IdeaReject } from './screens/IdeaReject';
import { IdeaCustom } from './screens/IdeaCustom';
import { ArticleDirection } from './screens/ArticleDirection';
import { ArticleGenerate } from './screens/ArticleGenerate';
import { ArticleEdit } from './screens/ArticleEdit';
import { TitleGenerate } from './screens/TitleGenerate';
import { SelectTitle } from './screens/SelectTitle';
import { IntroGenerate } from './screens/IntroGenerate';
import { SelectIntro } from './screens/SelectIntro';
import { ImageGenerate } from './screens/ImageGenerate';
import { SelectImage } from './screens/SelectImage';
import { BlogPreview } from './screens/BlogPreview';
import { Success } from './screens/Success';
import { AdminLogin } from './screens/admin/AdminLogin';
import { AdminDashboard } from './screens/admin/AdminDashboard';
import { AdminUsers } from './screens/admin/AdminUsers';
import { AdminUserDetail } from './screens/admin/AdminUserDetail';
import { AdminTemplates } from './screens/admin/AdminTemplates';
import { AdminConfig } from './screens/admin/AdminConfig';
import { AdminAISettings } from './screens/admin/AdminAISettings';
import { AdminPromptLogs } from './screens/admin/AdminPromptLogs';
import { AdminStorage } from './screens/admin/AdminStorage';
import { AdminCronjobMonitor } from './screens/admin/AdminCronjobMonitor';
import { AdminEmailDebugger } from './screens/admin/AdminEmailDebugger';
import { AdminCampaigns } from './screens/admin/AdminCampaigns';
import { AdminFinancial } from './screens/admin/AdminFinancial';
import { AdminBlogCostDetail } from './screens/admin/AdminBlogCostDetail';
import { AdminSupportUsers } from './screens/admin/AdminSupportUsers';
import { AdminBroadcast } from './screens/admin/AdminBroadcast';
import { AdminWebhooks } from './screens/admin/AdminWebhooks';
import { AdminErrors } from './screens/admin/AdminErrors';
import { AdminSocialMedia } from './screens/admin/AdminSocialMedia';
import { AdminChatbot } from './screens/admin/AdminChatbot';
import { AdminChatbotKnowledge } from './screens/admin/AdminChatbotKnowledge';
import { AdminChatbotLogs } from './screens/admin/AdminChatbotLogs';
import { AdminChatbotSupport } from './screens/admin/AdminChatbotSupport';
import { AdminChatbotConfig } from './screens/admin/AdminChatbotConfig';
import { AdminWhatsApp } from './screens/admin/AdminWhatsApp';
import { AdminGuard } from './components/AdminGuard';
import { SupportGuard } from './components/SupportGuard';
import { UserGuard } from './components/UserGuard';
import { BlogPost } from './screens/public/BlogPost';
import { UserBlogList } from './screens/public/UserBlogList';
import { Unsubscribe } from './screens/public/Unsubscribe';
import { DomainSetup } from './screens/public/DomainSetup';
import { SubscribeForm } from './screens/public/SubscribeForm';
import { MyBlogs } from './screens/MyBlogs';
import { NewBlogChoice } from './screens/NewBlogChoice';
import { IdeaGenerate } from './screens/IdeaGenerate';
import { TextImport } from './screens/TextImport';
import { BlogEdit } from './screens/BlogEdit';
import { EmailCampaigns } from './screens/EmailCampaigns';
import { CreateCampaignChoice } from './screens/CreateCampaignChoice';
import { CreateCampaignFromBlog } from './screens/CreateCampaignFromBlog';
import CampaignVariantSelection from './screens/CampaignVariantSelection';
import CampaignVariantGenerate from './screens/CampaignVariantGenerate';
import { CreateCampaignMultiBlog } from './screens/CreateCampaignMultiBlog';
import CampaignMultiBlogGenerate from './screens/CampaignMultiBlogGenerate';
import { ManualCampaignCreate } from './screens/ManualCampaignCreate';
import { CampaignDetail } from './screens/CampaignDetail';
import { CampaignEdit } from './screens/CampaignEdit';
import { Mailinglist } from './screens/Mailinglist';
import SocialMediaCreateChoice from './screens/SocialMediaCreateChoice';
import SocialMediaCreate from './screens/SocialMediaCreate';
import SocialMediaEdit from './screens/SocialMediaEdit';
import SocialMediaPosts from './screens/SocialMediaPosts';
import { RootRedirect } from './components/RootRedirect';
import { KnowledgeOverlay } from './components/KnowledgeOverlay';
import { ConditionalFloatingButton } from './components/ConditionalFloatingButton';
import { AdminKnowledgeBase } from './screens/admin/AdminKnowledgeBase';
import { AdminSignups } from './screens/admin/AdminSignups';
import { AdminApiDocumentation } from './screens/admin/AdminApiDocumentation';
import { AdminHandleiding } from './screens/admin/AdminHandleiding';
import { AdminCancellations } from './screens/admin/AdminCancellations';
import { AdminKlanttraject } from './screens/admin/AdminKlanttraject';
import { AdminKlanttrajectBeheer } from './screens/admin/AdminKlanttrajectBeheer';
import { SupportKlanttraject } from './screens/support/SupportKlanttraject';
import OnboardingWizard from './screens/OnboardingWizard';
import { Activiteit } from './screens/Activiteit';
import { Ideas } from './screens/Ideas';
import { SubscriptionEnded } from './screens/SubscriptionEnded';
import { AanDeSlagPanel } from './components/aandeslag/AanDeSlagPanel';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SessionProvider>
          <ToastProvider>
          <ErrorBoundary>
            <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/verify" element={<AuthVerify />} />

            {/* Onboarding Route */}
            <Route path="/onboarding" element={<OnboardingWizard />} />
            <Route path="/abonnement-gestopt" element={<SubscriptionEnded />} />

            {/* Dashboard Routes */}
            <Route path="/dashboard" element={<UserGuard><Dashboard /></UserGuard>} />
            <Route path="/aan-de-slag" element={<UserGuard><AanDeSlagPanel /></UserGuard>} />
            <Route path="/dashboard/settings" element={<UserGuard><DashboardSettings /></UserGuard>} />
            <Route path="/dashboard/social" element={<Navigate to="/dashboard/settings?tab=socials" replace />} />
            <Route path="/dashboard/new-blog" element={<UserGuard><NewBlogChoice /></UserGuard>} />
            <Route path="/idea-generate" element={<UserGuard><IdeaGenerate /></UserGuard>} />
            <Route path="/text-import" element={<UserGuard><TextImport /></UserGuard>} />

            {/* Root redirect to dashboard or login */}
            <Route path="/" element={<RootRedirect />} />

            {/* Wizard Routes (Magic Link Flow) */}
            <Route path="/idea-select" element={<UserGuard><IdeaSelection /></UserGuard>} />
            <Route path="/idea-check" element={<UserGuard><IdeaCheck /></UserGuard>} />
            <Route path="/idea-reject" element={<UserGuard><IdeaReject /></UserGuard>} />
            <Route path="/idea-custom" element={<UserGuard><IdeaCustom /></UserGuard>} />
            <Route path="/article-direction" element={<UserGuard><ArticleDirection /></UserGuard>} />
            <Route path="/article-generate" element={<UserGuard><ArticleGenerate /></UserGuard>} />
            <Route path="/article-edit" element={<UserGuard><ArticleEdit /></UserGuard>} />
            <Route path="/title-generate" element={<UserGuard><TitleGenerate /></UserGuard>} />
            <Route path="/select-title" element={<UserGuard><SelectTitle /></UserGuard>} />
            <Route path="/intro-generate" element={<UserGuard><IntroGenerate /></UserGuard>} />
            <Route path="/select-intro" element={<UserGuard><SelectIntro /></UserGuard>} />
            <Route path="/image-generate" element={<UserGuard><ImageGenerate /></UserGuard>} />
            <Route path="/select-image" element={<UserGuard><SelectImage /></UserGuard>} />
            <Route path="/blog-preview" element={<UserGuard><BlogPreview /></UserGuard>} />
            <Route path="/success" element={<UserGuard><Success /></UserGuard>} />

            {/* Public Blog Routes */}
            <Route path="/my-blogs" element={<UserGuard><MyBlogs /></UserGuard>} />
            <Route path="/blog/:username" element={<UserBlogList />} />
            <Route path="/blog/:username/:slug" element={<BlogPost />} />
            <Route path="/blog/edit/:blogId" element={<UserGuard><BlogEdit /></UserGuard>} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/domain-setup/:token" element={<DomainSetup />} />
            <Route path="/inschrijven/:username" element={<SubscribeForm />} />

            {/* Email Campaign Routes */}
            <Route path="/campaigns" element={<UserGuard><EmailCampaigns /></UserGuard>} />
            <Route path="/campaigns/create" element={<UserGuard><CreateCampaignChoice /></UserGuard>} />
            <Route path="/campaigns/create/from-blog" element={<UserGuard><CreateCampaignFromBlog /></UserGuard>} />
            <Route path="/campaign/create-from-blog" element={<UserGuard><CreateCampaignFromBlog /></UserGuard>} />
            <Route path="/campaign/variant-select" element={<UserGuard><CampaignVariantSelection /></UserGuard>} />
            <Route path="/campaign/create-from-blog/generate" element={<UserGuard><CampaignVariantGenerate /></UserGuard>} />
            <Route path="/campaigns/create/multi-blog" element={<UserGuard><CreateCampaignMultiBlog /></UserGuard>} />
            <Route path="/campaigns/create/multi-blog/generate" element={<UserGuard><CampaignMultiBlogGenerate /></UserGuard>} />
            <Route path="/campaigns/create/manual" element={<UserGuard><ManualCampaignCreate /></UserGuard>} />
            <Route path="/campaigns/edit/:campaignId" element={<UserGuard><CampaignEdit /></UserGuard>} />
            <Route path="/campaigns/:campaignId" element={<UserGuard><CampaignDetail /></UserGuard>} />
            <Route path="/mailinglist" element={<UserGuard><Mailinglist /></UserGuard>} />

            {/* Social Media Routes */}
            <Route path="/social-media/posts" element={<UserGuard><SocialMediaPosts /></UserGuard>} />
            <Route path="/social-media/create" element={<UserGuard><SocialMediaCreateChoice /></UserGuard>} />
            <Route path="/social-media/create/:blogId" element={<UserGuard><SocialMediaCreate /></UserGuard>} />
            <Route path="/social-media/edit/:postId" element={<UserGuard><SocialMediaEdit /></UserGuard>} />

            {/* Activity Route */}
            <Route path="/activiteit" element={<UserGuard><Activiteit /></UserGuard>} />

            {/* Ideeën Route */}
            <Route path="/ideeen" element={<UserGuard><Ideas /></UserGuard>} />

            {/* Mijn vragen Route */}

            {/* Support User Routes */}
            <Route path="/support" element={<Navigate to="/support/users" replace />} />
            <Route path="/support/users" element={<SupportGuard><AdminUsers /></SupportGuard>} />
            <Route path="/support/users/:userId" element={<SupportGuard><AdminUserDetail /></SupportGuard>} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
            <Route path="/admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
            <Route path="/admin/users/:userId" element={<AdminGuard><AdminUserDetail /></AdminGuard>} />
            <Route path="/admin/templates" element={<AdminGuard><AdminTemplates /></AdminGuard>} />
            <Route path="/admin/config" element={<AdminGuard><AdminConfig /></AdminGuard>} />
            <Route path="/admin/ai-settings" element={<AdminGuard><AdminAISettings /></AdminGuard>} />
            <Route path="/admin/prompt-logs" element={<AdminGuard><AdminPromptLogs /></AdminGuard>} />
            <Route path="/admin/storage" element={<AdminGuard><AdminStorage /></AdminGuard>} />
            <Route path="/admin/cronjob-monitor" element={<AdminGuard><AdminCronjobMonitor /></AdminGuard>} />
            <Route path="/admin/email-debugger" element={<AdminGuard><AdminEmailDebugger /></AdminGuard>} />
            <Route path="/admin/webhooks" element={<AdminGuard><AdminWebhooks /></AdminGuard>} />
            <Route path="/admin/signups" element={<AdminGuard><AdminSignups /></AdminGuard>} />
            <Route path="/admin/errors" element={<AdminGuard><AdminErrors /></AdminGuard>} />
            <Route path="/admin/campaigns" element={<AdminGuard><AdminCampaigns /></AdminGuard>} />
            <Route path="/admin/financial" element={<AdminGuard><AdminFinancial /></AdminGuard>} />
            <Route path="/admin/financial/blog/:blogId" element={<AdminGuard><AdminBlogCostDetail /></AdminGuard>} />
            <Route path="/admin/support-users" element={<AdminGuard><AdminSupportUsers /></AdminGuard>} />
            <Route path="/admin/broadcast" element={<AdminGuard><AdminBroadcast /></AdminGuard>} />
            <Route path="/admin/social-media" element={<AdminGuard><AdminSocialMedia /></AdminGuard>} />
            <Route path="/admin/chatbot" element={<AdminGuard><AdminChatbot /></AdminGuard>} />
            <Route path="/admin/chatbot-knowledge" element={<AdminGuard><AdminChatbotKnowledge /></AdminGuard>} />
            <Route path="/admin/chatbot-logs" element={<AdminGuard><AdminChatbotLogs /></AdminGuard>} />
            <Route path="/admin/chatbot-support" element={<AdminGuard><AdminChatbotSupport /></AdminGuard>} />
            <Route path="/admin/chatbot-config" element={<AdminGuard><AdminChatbotConfig /></AdminGuard>} />
            <Route path="/admin/kennisbank" element={<AdminGuard><AdminKnowledgeBase /></AdminGuard>} />
            <Route path="/admin/api-documentation" element={<AdminGuard><AdminApiDocumentation /></AdminGuard>} />
            <Route path="/admin/handleiding" element={<AdminGuard><AdminHandleiding /></AdminGuard>} />
            <Route path="/support/handleiding" element={<SupportGuard><AdminHandleiding /></SupportGuard>} />
            <Route path="/admin/cancellations" element={<AdminGuard><AdminCancellations /></AdminGuard>} />
            <Route path="/admin/klanttraject" element={<AdminGuard><AdminKlanttraject /></AdminGuard>} />
            <Route path="/admin/klanttraject/beheer" element={<AdminGuard><AdminKlanttrajectBeheer /></AdminGuard>} />
            <Route path="/support/klanttraject" element={<SupportGuard><SupportKlanttraject /></SupportGuard>} />
            <Route path="/admin/whatsapp" element={<AdminGuard><AdminWhatsApp /></AdminGuard>} />
            <Route path="/support/whatsapp" element={<SupportGuard><AdminWhatsApp /></SupportGuard>} />
              </Routes>
            </ErrorBoundary>
          </ToastProvider>
        </SessionProvider>
      </AuthProvider>
      <KnowledgeOverlay />
      <ConditionalFloatingButton />
    </BrowserRouter>
  );
}

export default App;
